import { useMsal } from '@azure/msal-react';
import { Button, Link, mergeClasses, Text } from '@fluentui/react-components';
import { AddCircleRegular, ArrowDownloadFilled, DismissRegular } from '@fluentui/react-icons';
import parse from 'html-react-parser';
import { observer } from 'mobx-react-lite';
import React, { ReactElement, useState } from 'react';
import { calculateBytesFromMB } from '../../../common/calculation/calculate-bytes-in-mb';
import { downloadFile } from '../../../common/file/download-file';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { getCleanHtmlString } from '../../../common/html-string';
import { getTooltipContentByFieldName } from '../../../common/tooltip/tooltip-get-content';
import { DocumentType } from '../../../common/types/document-types';
import { executiveSummaryDocumentConfiguration, supportingDocumentConfiguration } from '../../../configurations';
import { DocumentMetadata } from '../../../models';
import { ResourceTicketRequest, ResourceTypes } from '../../../models/resource-services';
import { isPdfFileName, isValidImageFile } from '../../../shared/functions';
import { UploadButton } from '../../shared-components';
import DocumentFile from '../../shared-components/DocumentFile';
import { FlexBox } from '../../shared-components/flex-box';
import { FormattedInput, FormInput } from '../../shared-components/form';
import { RichTextEditor } from '../../shared-components/RichTextEditor';
import { ValidationMessageList } from '../../shared-components/ValidationMessageList';
import { CustomerReferenceForm } from './CustomerReferenceForm';
import './NominationSubmissionSection.scss';

const MAX_NOMINATION_WEBSITES_COUNT = 5;
const MAX_SUPPORTING_DOCUMENTS_COUNT = 1;

interface NominationSubmissionProps {
  exportNominationToPdf(): void;
}

const NominationSubmissionSectionImpl: React.FC<NominationSubmissionProps> = (props) => {
  const {
    exportNominationToPdf,
  } = props;

  const {
    instance,
  } = useMsal();

  const {
    apiStore,
    nominationWizardPageUiStore,
  } = useStores();

  const isBeingValidated = nominationWizardPageUiStore.isBeingValidated('nomination-submission');

  const commonStyles = useCommonStyles();

  const [nominationTemplateFileTypeInvalid, setNominationTemplateFileTypeInvalid] = useState(false);
  const [nominationTemplateSizeInvalid, setNominationTemplateSizeInvalid] = useState(false);
  const [selectedSupportingDocumentError, setSelectedSupportingDocumentError] = useState('');

  const getExecutiveSummaryDocumentMetadata = async (nominationId: number): Promise<void> => {
    try {
      const executiveSummaryDocument = await apiStore.getExecutiveSummaryDocumentMetadata(instance, nominationId);
      nominationWizardPageUiStore.setExecutiveSummaryDocument(executiveSummaryDocument);
    } catch (error) {
      // TODO: Handle error
    }
  };

  const handleUploadDocument = async (documentType: DocumentType, documentContent: File): Promise<void> => {
    nominationWizardPageUiStore.setLoadingType('save');
    try {
      if (documentType === 'executive-summary') {
        await apiStore.uploadExecutiveSummaryFileContent(
          instance,
          nominationWizardPageUiStore.nomination.nominationId,
          documentContent,
        );
        await getExecutiveSummaryDocumentMetadata(nominationWizardPageUiStore.nomination.nominationId);
      } else {
        const documentMetadata = await apiStore.uploadSupportingDocument(
          instance,
          nominationWizardPageUiStore.nomination.nominationId,
          documentContent,
        );
        nominationWizardPageUiStore.nomination.supportingDocuments.push(documentMetadata);
      }
    } catch (error) {
      // TODO: Handle error
    } finally {
      nominationWizardPageUiStore.setLoadingType(undefined);
    }
  };

  const uploadFile = async (documentType: DocumentType, files: FileList): Promise<void> => {
    if (!files || !files[0]) {
      return;
    }

    const file = files[0];
    const isValidImage = await isValidImageFile(file);

    if (documentType === 'executive-summary') {
      if (!executiveSummaryDocumentConfiguration.supportedFileTypes.includes(file.type) || !isValidImage) {
        setNominationTemplateFileTypeInvalid(true);
        return;
      } else {
        setNominationTemplateFileTypeInvalid(false);
      }

      if (file.size > calculateBytesFromMB(executiveSummaryDocumentConfiguration.maxFileSizeInMB)) {
        setNominationTemplateSizeInvalid(true);
        return;
      } else {
        setNominationTemplateSizeInvalid(false);
      }
    } else if (documentType === 'supporting-document') {
      if (nominationWizardPageUiStore.nomination.supportingDocuments.length >= MAX_SUPPORTING_DOCUMENTS_COUNT) {
        setSelectedSupportingDocumentError('Upload only one supporting document for each nomination.');
        return;
      } else if (!supportingDocumentConfiguration.supportedFileTypes.includes(file.type)) {
        setSelectedSupportingDocumentError('Select a supported file type.');
        return;
      } else if (file.size > calculateBytesFromMB(supportingDocumentConfiguration.maxFileSizeInMB)) {
        setSelectedSupportingDocumentError('Upload a supporting document that is 10 MB or smaller.');
        return;
      } else if (!isValidImage) {
        setSelectedSupportingDocumentError('Select a valid image file.');
        return;
      } else {
        setSelectedSupportingDocumentError('');
      }
    }

    await handleUploadDocument(documentType, file);
  }

  const handleDownload = async (request: ResourceTicketRequest): Promise<void> => {
    nominationWizardPageUiStore.setLoadingType('save');
    try {
      const resourceTicket = await apiStore.postResourceTicket(instance, request);
      downloadFile(resourceTicket.ticketId);
    } catch (error) {
      // TODO: Handle error
    } finally {
      nominationWizardPageUiStore.setLoadingType(undefined);
    }
  };

  const handleDownloadExecutiveSummary = async (): Promise<void> => {
    if (!nominationWizardPageUiStore.executiveSummaryDocument) return;

    await handleDownload({
      data: {
        routeData: {
          nominationId: nominationWizardPageUiStore.executiveSummaryDocument.nominationId,
        },
        queryString: {
          isDisplayedAsInline: `${isPdfFileName(nominationWizardPageUiStore.executiveSummaryDocument.fileName)}`,
        },
      },
      resourceType: ResourceTypes.ExecutiveSummary,
    });
  };

  const handleDownloadAwardExecutiveSummary = async (): Promise<void> => {
    await handleDownload({
      data: {
        routeData: {
          awardId: nominationWizardPageUiStore.nomination.award.awardId,
          nominationId: nominationWizardPageUiStore.nomination.nominationId,
        },
      },
      resourceType: ResourceTypes.AwardExecutiveSummary,
    });
  };

  const handleDownloadSupportingDocument = async (supportingDocumentId: number, fileName: string): Promise<void> => {
    await handleDownload({
      data: {
        routeData: {
          supportingDocumentId,
        },
        queryString: {
          isDisplayedAsInline: `${isPdfFileName(fileName)}`,
        },
      },
      resourceType: ResourceTypes.SupportingDocument,
    });
  };

  const handleDeleteExecutiveSummary = async (): Promise<void> => {
    if (!nominationWizardPageUiStore.executiveSummaryDocument) return;

    nominationWizardPageUiStore.setLoadingType('save');
    try {
      await apiStore.deleteExecutiveSummaryFileContent(
        instance,
        nominationWizardPageUiStore.executiveSummaryDocument.nominationId,
      );
      nominationWizardPageUiStore.setExecutiveSummaryDocument(undefined);
    } catch (error) {
      // TODO: Handle error
    } finally {
      nominationWizardPageUiStore.setLoadingType(undefined);
    }
  };

  const handleDeleteSupportingDocument = async (supportingDocumentId: number): Promise<void> => {
    nominationWizardPageUiStore.setLoadingType('save');
    try {
      await apiStore.deleteSupportingDocument(instance, supportingDocumentId);
      const supportingDocumentIndex = nominationWizardPageUiStore.nomination.supportingDocuments.findIndex(
        supportingDocument => supportingDocument.documentId === supportingDocumentId,
      );

      if (supportingDocumentIndex >= 0) {
        nominationWizardPageUiStore.nomination.supportingDocuments.splice(supportingDocumentIndex, 1);
      }
    } catch (error) {
      // TODO: Handle error
    } finally {
      nominationWizardPageUiStore.setLoadingType(undefined);
    }
  };

  const generateExecutiveSummaryFileInputValidation = (): string => {
    if (nominationTemplateSizeInvalid) return `Upload a document that is ${executiveSummaryDocumentConfiguration.maxFileSizeInMB} MB or smaller.`;
    if (nominationTemplateFileTypeInvalid) return 'Select a supported file type.';
    if (!isBeingValidated) return '';
    return nominationWizardPageUiStore.executiveSummaryDocumentErrorMessage;
  }

  const renderNominationWebsites = (): ReactElement => {
    return (
      <FlexBox
        aligns='start'
        direction='column'
        className='submission__websites'
      >
        <Text
          weight='semibold'
        >
          Website where solution is listed
        </Text>
        <Button
          appearance='primary'
          icon={<AddCircleRegular />}
          disabled={nominationWizardPageUiStore.nomination.websites.length >= MAX_NOMINATION_WEBSITES_COUNT}
          onClick={() => nominationWizardPageUiStore.addNominationWebsite()}
        >
          Add link
        </Button>
        {
          nominationWizardPageUiStore.nomination.websites.map((website, index) => {
            const websiteValidation = nominationWizardPageUiStore.nominationWebsiteValidations[index];
            const rowKey = nominationWizardPageUiStore.nominationWebsiteRowKeys[index];

            return (
              <FlexBox
                key={rowKey}
                aligns='center'
              >
                <FormattedInput
                  prefix='https://'
                  label={`Link #${index + 1}`}
                  onChange={websiteUrl => nominationWizardPageUiStore.nomination.websites[index].websiteURL = websiteUrl}
                  value={nominationWizardPageUiStore.nomination.websites[index].websiteURL}
                  isValidated={isBeingValidated}
                  contentAfter={(
                    <Button
                      appearance='subtle'
                      title='Delete'
                      icon={<DismissRegular />}
                      onClick={() => {
                        nominationWizardPageUiStore.removeNominationWebsite(index);
                      }}
                    />
                  )}
                  errorText={
                    isBeingValidated && websiteValidation.fieldErrors.websiteURL?.length
                      ? <ValidationMessageList messages={websiteValidation.fieldErrors.websiteURL} />
                      : undefined
                  }
                />
              </FlexBox>
            );
          })
        }

      </FlexBox>
    )
  }

  const renderExecutiveSummaryDocument = (documentMetadata: DocumentMetadata): ReactElement => (
    <DocumentFile
      onDownloadFile={handleDownloadExecutiveSummary}
      onDeleteFile={handleDeleteExecutiveSummary}
      filename={documentMetadata.fileName}
    />
  );

  return (
      <FlexBox
        isFullWidth
        direction='column'
        aligns='start'
        className='nomination-submission-section'
      >
        <FlexBox
          aligns='start'
          direction='column'
          isFullWidth
          className='nomination-submission-section__submission'
        >
          <FlexBox
            wrap
            isFullWidth
            content='space-between'
            aligns='start'
            className='submission__solution'
          >
            <FormInput
              required
              label='Solution name'
              className='field--solution'
              validationMessage={
                isBeingValidated && nominationWizardPageUiStore.nominationSubmissionValidation.fieldErrors.name?.length
                  ? <ValidationMessageList messages={nominationWizardPageUiStore.nominationSubmissionValidation.fieldErrors.name} />
                  : undefined
              }
              onChange={(name) => nominationWizardPageUiStore.nomination.name = name}
              inputProps={{
                maxLength: 100,
                value: nominationWizardPageUiStore.nomination.name,
              }}
            />
            {
              !nominationWizardPageUiStore.nomination.award.requireWrittenNomination && (
                <Button
                  appearance='secondary'
                  onClick={handleDownloadAwardExecutiveSummary}
                  icon={<ArrowDownloadFilled color='#0078D4' />}
                >
                  Download template
                </Button>
              )
            }
          </FlexBox>

          <div className={mergeClasses(commonStyles.richTextContent, 'submission__award-summary')}>
            {parse(getCleanHtmlString(nominationWizardPageUiStore.nomination.award.detail))}
          </div>
          {
            nominationWizardPageUiStore.nomination.award.requireWrittenNomination &&
            <RichTextEditor
              value={nominationWizardPageUiStore.writtenNominationContent}
              inputTitle='Nomination summary text field'
              onChange={content => nominationWizardPageUiStore.setWrittenNomination(content)}
              errorMessage={
                isBeingValidated && nominationWizardPageUiStore.nominationSubmissionValidation.fieldErrors.writtenNominationContent?.length
                  ? <ValidationMessageList messages={nominationWizardPageUiStore.nominationSubmissionValidation.fieldErrors.writtenNominationContent} />
                  : undefined
              }
              showCharacterCounterWithMaxlength={8000}
            />
          }
          <FlexBox
            direction='column'
            className='submission__winner-summary'
          >
            <div>
              <b>Winner summary</b>
              <Text
                as='p'
                block
              >
                If you are selected as a winner, your response to the prompt below will be published as a short,
                500-character summary of your solution and its impact.
                This summary will be included within the <b>publicly available</b> summary document referencing this year’s winners – made available on our website later this year. <b className='text--red'>IMPORTANT:</b> <b>do not include</b> any information in this section referencing your organization’s financial information, customer names or references, or other sensitive information that is not approved to be shared publicly.
              </Text>
            </div>
            <Text
              block
              as='p'
            >
              <b>Note:</b> Microsoft may edit this content to align to our writing guidelines and to ensure consistency, spelling, and grammar.
            </Text>
            <Text>
              {'For examples, please view the '}
              <Link
                href='https://aka.ms/POTYA2024_WinnersFinalists_List'
                target='_blank'
                rel='noopener noreferrer'
                className='link'
              >
                nomination overview deck slide
              </Link>.
            </Text>
          </FlexBox>

          <FormInput
            validationMessage={
              isBeingValidated && nominationWizardPageUiStore.nominationSubmissionValidation.fieldErrors.abstract?.length
                ? <ValidationMessageList messages={nominationWizardPageUiStore.nominationSubmissionValidation.fieldErrors.abstract} />
                : undefined
            }
            onChange={(abstract) => nominationWizardPageUiStore.nomination.abstract = abstract}
            textAreaProps={{
              required: true,
              value: nominationWizardPageUiStore.nomination.abstract || '',
              'aria-label': 'nomination abstract field',
              placeholder: 'nomination abstract field',
              maxLength: 500,
              spellCheck: true,
            }}
          />

          <Link
            as='button'
            className={commonStyles.buttonAsLink}
            onClick={exportNominationToPdf}
          >
            Preview as PDF
          </Link>
          {
            !nominationWizardPageUiStore.nomination.award.requireWrittenNomination && (
              <UploadButton
                onUpload={(fileList) => uploadFile('executive-summary', fileList)}
                validationMessage={generateExecutiveSummaryFileInputValidation()}
                infoLabel={getTooltipContentByFieldName('nomination-submission', 'uploadTemplate')}
                buttonProps={{
                  appearance: 'primary',
                  children: 'Upload template',
                  icon: (<AddCircleRegular />),
                  disabled: nominationWizardPageUiStore.hasExecutiveSummaryDocument,
                }}
              >
                {
                  nominationWizardPageUiStore.executiveSummaryDocument
                    ? renderExecutiveSummaryDocument(nominationWizardPageUiStore.executiveSummaryDocument)
                    : null
                }
              </UploadButton>
            )
          }
          <UploadButton
            onUpload={(fileList) => uploadFile('supporting-document', fileList)}
            validationMessage={selectedSupportingDocumentError}
            infoLabel={getTooltipContentByFieldName('nomination-submission', 'uploadSupportingDocument')}
            buttonProps={{
              appearance: 'primary',
              children: 'Upload supporting document',
              icon: (<AddCircleRegular />),
            }}
          >
            {
              nominationWizardPageUiStore.nomination.supportingDocuments.map(supportingDocument => (
                <DocumentFile
                  onDownloadFile={() => handleDownloadSupportingDocument(
                    supportingDocument.documentId,
                    supportingDocument.fileName,
                  )}
                  onDeleteFile={() => handleDeleteSupportingDocument(supportingDocument.documentId)}
                  key={supportingDocument.documentId}
                  filename={supportingDocument.fileName}
                />
              ))
            }
          </UploadButton>
          {renderNominationWebsites()}
        </FlexBox>
        {
          nominationWizardPageUiStore.nomination.award.requireCustomerReference &&
          <CustomerReferenceForm />
        }
      </FlexBox>
  );
}

export const NominationSubmissionSection = observer(NominationSubmissionSectionImpl);
